import { useEffect } from 'react';

// Listen for add-to-homework events from embeds (ensured after allEditors init)
export function useAddToHomeworkListener({
  currentTab, whiteboards, setWhiteboards, setHomeworkContainers,
  allEditorsRef, whiteboardsRef, setCurrentTab, homeworkContainers, allEditors,
}) {
  useEffect(() => {
    const handler = (e) => {
      try {
        const detail = e.detail || {};
        const { type, attrs } = detail;
        console.log('LessonWorkspace: Received add-to-homework event', { type, attrs });
        // 1) Pick the target homework tab.
        //    Priority:
        //      a) the homework tab the teacher is currently viewing (so a
        //         "+ Add to homework" click on a lesson exercise lands in
        //         the homework the teacher is editing right now)
        //      b) the first existing homework tab (legacy fallback for
        //         clicks made while the lesson tab is active)
        //      c) create a brand new homework tab if none exist
        let targetHomeworkId;
        const currentWb = currentTab > 0 ? whiteboards[currentTab - 1] : null;
        const fallbackHomeworkTab = whiteboards.find(wb => wb.type === 'homework');
        if (currentWb && currentWb.type === 'homework') {
          targetHomeworkId = currentWb.id;
        } else if (fallbackHomeworkTab) {
          targetHomeworkId = fallbackHomeworkTab.id;
        } else {
          targetHomeworkId = Date.now();
          const newTab = { id: targetHomeworkId, type: 'homework', title: 'Homework' };
          setWhiteboards(prev => [...prev, newTab]);
          setHomeworkContainers(prev => ({ ...prev, [targetHomeworkId]: [] }));
        }

        // 2) Always create a NEW homework container for each added exercise
        const containerId = `${Date.now()}-${Math.floor(Math.random()*1000)}`;
        setHomeworkContainers(prev => {
          const list = prev[targetHomeworkId] || [];
          return { ...prev, [targetHomeworkId]: [...list, { id: containerId }] };
        });

        // 3) Poll for editor and insert content once ready, then switch tab
        const editorId = `homework-${targetHomeworkId}-${containerId}`;
        const typeToNode = {
          abcd: 'abcdExercise',
          openQuestions: 'openQuestionsExercise',
          quiz: 'quizNode',
          wordDefinition: 'wordDefinitionExercise',
          fillInTheGaps: 'fillInTheGapsExercise',
          trueFalse: 'trueFalseExercise',
          warmupdiscussion: 'warmUpDiscussionExercise',
          createdialogue: 'createDialogueExercise',
          essentialvocabulary: 'essentialVocabularyExercise',
          fourOpinions: 'fourOpinionsExercise',
          communicativeSituations: 'communicativeSituations',
          wordTranslation: 'wordTranslationExercise',
          matchingHalves: 'matchingHalves',
          errorCorrection: 'errorCorrection',
          grammarRules: 'grammarRulesExercise',
          proscons: 'prosConsExercise',
          jumbledSentence: 'jumbledSentence',
          multipleChoice: 'multipleChoice',
        };
        const nodeType = typeToNode[type];
        console.log('LessonWorkspace: Mapping type to node', { type, nodeType });
        const content = nodeType ? { type: nodeType, attrs } : attrs;
        const maxAttempts = 40;
        const tryInsert = (attempt = 0) => {
          const editor = allEditorsRef.current.get(editorId);
          if (editor) {
            editor.chain().focus().insertContent(content).run();
            const wbArr = whiteboardsRef.current || [];
            const tabIndex = (wbArr.findIndex(wb => wb.id === targetHomeworkId) + 1) || (wbArr.length + 1);
            if (tabIndex > 0) setCurrentTab(tabIndex);
            return;
          }
          if (attempt < maxAttempts) {
            setTimeout(() => tryInsert(attempt + 1), 75);
          } else {
            // Fallback: just switch to homework tab even if editor not yet resolved
            const wbArr2 = whiteboardsRef.current || [];
            const idx = (wbArr2.findIndex(wb => wb.id === targetHomeworkId) + 1) || (wbArr2.length + 1);
            if (idx > 0) setCurrentTab(idx);
          }
        };
        tryInsert();
      } catch (err) {
        console.error('Failed to add to homework:', err);
      }
    };
    window.addEventListener('novas:add-to-homework', handler);
    return () => window.removeEventListener('novas:add-to-homework', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whiteboards, homeworkContainers, allEditors, setCurrentTab, currentTab]);
}
